import React, { createContext, useContext, useMemo } from 'react';
import * as yup from 'yup';

import useDisclosure from '@/core/hooks/useDisclosure';
import { useNativeContext } from '@/core/lib/native/native.context';
import Query from '@/core/lib/new-architecture/query';
import Store from '@/core/lib/new-architecture/store';
import { donationsCategoryResultSchema, UserSearchesSearchesResult, UserSearchesSearchResult } from '@/core/types/donation';
import { locationFormSchema } from '@/core/types/geo';

export const catalogueSearchSchema = yup.object({
  loc: locationFormSchema.nullable().default(null),
  tag: yup.string().optional(),
  cat: donationsCategoryResultSchema.nullable().default(null),
  title: yup.string().max(100, 'errors.inputs.max-100').nullable(),
});

export type CatalogueSearchSchema = yup.InferType<typeof catalogueSearchSchema>;

export interface CatalogueSearchContextType {
  isOpen?: boolean;
  onOpen: () => void;
  onClose: () => void;
  data: CatalogueSearchSchema;
  userSearches: UserSearchesSearchResult[];
}

const getDonationsUserSearches = (donationsUserSearches?: UserSearchesSearchesResult): UserSearchesSearchResult[] => {
  if (donationsUserSearches?.searches) {
    return donationsUserSearches.searches;
  }

  return [];
};

export const CatalogueSearchContext = createContext<CatalogueSearchContextType | null>(null);

export const useCatalogueSearchContext = () => useContext(CatalogueSearchContext) as CatalogueSearchContextType;

const CatalogueSearchProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { isOpen, onOpen: onOpenState, onClose: onCloseState } = useDisclosure();

  const { setShouldHideKeyboard } = useNativeContext();
  const { requestToken } = Store.auth.useRequest();
  const { data: current } = Store.current.useCurrent();
  const { data: donationsUserSearches } = Query.user.useDonationsUserSearches({ enabled: !!current, token: requestToken });
  const { data: catalogue } = Store.catalogue.useCatalogue();
  const userSearches = getDonationsUserSearches(donationsUserSearches);

  const data = catalogue?.search ?? {
    loc: null,
    cat: null,
    title: null,
  };

  const onOpen = () => {
    onOpenState();
    document.body.style.overflow = 'hidden';
    setShouldHideKeyboard(true);
  };

  const onClose = () => {
    onCloseState();
    document.body.style.overflow = '';
    setShouldHideKeyboard(false);
  };

  const value = useMemo(
    () => ({
      isOpen,
      onOpen,
      onClose,
      data,
      userSearches,
    }),
    [isOpen, data],
  );

  return <CatalogueSearchContext.Provider value={value}>{children}</CatalogueSearchContext.Provider>;
};

export default CatalogueSearchProvider;
